import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Instagram, ArrowLeft, Phone } from "lucide-react";
import { pub } from "../lib/crmClient";

const PAGE = 12;

function Tile({ post, i }) {
    const isVideo = post.media_type === "VIDEO";
    const img = isVideo ? post.thumbnail_url : post.media_url;
    return (
        <motion.a
            href={post.permalink} target="_blank" rel="noopener noreferrer"
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, delay: (i % 4) * 0.05, ease: [0.19, 1, 0.22, 1] }}
            className="group block relative aspect-square overflow-hidden border border-white/10 bg-black"
        >
            {img && <img src={img} alt={post.caption?.slice(0, 120) || "PaintKing project"} loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />}
            <div className="absolute top-2 right-2 flex gap-1">
                {isVideo && <span className="glass-chip border border-white/40 px-2 py-0.5 font-mono text-[9px] tracking-[0.28em] uppercase">{post.media_product_type === "REELS" ? "Reel" : "Video"}</span>}
                {post.media_type === "CAROUSEL_ALBUM" && <span className="glass-chip border border-white/40 px-2 py-0.5 font-mono text-[9px] tracking-[0.28em] uppercase">Carousel</span>}
                {post.pinned && <span className="bg-[#FFE600] text-black px-2 py-0.5 font-mono text-[9px] tracking-[0.28em] uppercase">★</span>}
            </div>
            {post.caption && (
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/80 to-transparent p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <p className="font-mono text-[10px] leading-snug text-white/90 line-clamp-3">{post.caption}</p>
                </div>
            )}
        </motion.a>
    );
}

export default function OurWork() {
    const [posts, setPosts] = useState([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [loading, setLoading] = useState(true);
    const [connected, setConnected] = useState(false);

    const loadMore = useCallback(async (reset = false) => {
        setLoading(true);
        try {
            const off = reset ? 0 : offset;
            const { data } = await pub.get(`/instagram/posts?limit=${PAGE}&offset=${off}`);
            setPosts(reset ? data.posts : [...posts, ...data.posts]);
            setTotal(data.total);
            setConnected(data.connected);
            setOffset(off + data.posts.length);
        } finally { setLoading(false); }
    }, [offset, posts]);

    useEffect(() => { loadMore(true); /* eslint-disable-next-line */ }, []);

    const hasMore = offset < total;

    return (
        <div data-testid="our-work" className="min-h-screen bg-black text-white">
            <header className="border-b border-white/10 sticky top-0 z-40 backdrop-blur-lg bg-black/60">
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
                    <Link to="/" className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-white/80 hover:text-[#FFE600]">
                        <ArrowLeft className="w-3.5 h-3.5" /> PaintKing · Home
                    </Link>
                    <a href="tel:5616984044" className="glass-btn inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.28em] uppercase border border-white/20 px-4 py-2">
                        <Phone className="w-3.5 h-3.5" /> 561 · 698 · 4044
                    </a>
                </div>
            </header>

            <section className="relative px-6 md:px-16 py-20 md:py-32">
                <div className="grid grid-cols-12 gap-6 items-end">
                    <div className="col-span-12 md:col-span-8">
                        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/50">[ Portfolio · /our-work ]</div>
                        <h1 className="font-display uppercase text-5xl md:text-7xl lg:text-8xl leading-[0.88] tracking-tight mt-4">
                            Every project.
                            <br />
                            <span className="rainbow-text">All in one place.</span>
                        </h1>
                    </div>
                    <div className="col-span-12 md:col-span-4">
                        <p className="font-display text-lg leading-tight text-white/85 max-w-md">
                            A living portfolio — every new Instagram post Paul publishes shows up here automatically. Residential, commercial, interior, exterior.
                        </p>
                        <a href="https://www.instagram.com/paint_king/" target="_blank" rel="noopener noreferrer"
                            data-testid="our-work-follow"
                            className="mt-5 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.28em] uppercase border-b border-white/40 hover:border-[#FFE600] hover:text-[#FFE600] pb-1 transition-colors">
                            <Instagram className="w-4 h-4" /> Follow @paint_king
                        </a>
                    </div>
                </div>
            </section>

            <section className="px-6 md:px-16 pb-20">
                {!connected && posts.length === 0 && !loading ? (
                    <div className="glass border border-white/15 p-10 md:p-16 text-center">
                        <Instagram className="w-10 h-10 mx-auto text-[#FFE600]" strokeWidth={1.4} />
                        <h3 className="font-display uppercase text-3xl md:text-4xl leading-tight tracking-tight mt-4">Warming up.</h3>
                        <p className="font-mono text-[12px] text-white/60 mt-3 max-w-xl mx-auto">The feed is being connected. See everything on Instagram meanwhile.</p>
                    </div>
                ) : (
                    <div data-testid="our-work-grid" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                        {posts.map((p, i) => <Tile key={p.id} post={p} i={i} />)}
                    </div>
                )}

                {hasMore && (
                    <div className="mt-12 text-center">
                        <button
                            data-testid="our-work-load-more"
                            onClick={() => loadMore(false)}
                            disabled={loading}
                            className="group relative overflow-hidden border border-white/80 hover:border-[#FFE600] px-10 py-4 font-mono text-[11px] tracking-[0.3em] uppercase disabled:opacity-50"
                        >
                            <span className="relative z-10 group-hover:text-black transition-colors">
                                {loading ? "Loading…" : "Load More Projects →"}
                            </span>
                            <span className="absolute inset-0 bg-[#FFE600] scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500" />
                        </button>
                    </div>
                )}
            </section>

            <section className="px-6 md:px-16 pb-24">
                <div className="glass border border-white/15 p-8 md:p-12 text-center max-w-3xl mx-auto">
                    <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#FFE600]">Inspired by what you see?</div>
                    <h3 className="font-display uppercase text-3xl md:text-5xl leading-tight tracking-tight mt-3">
                        Let's talk <span className="rainbow-text">your project.</span>
                    </h3>
                    <a href="/#whatsapp" data-testid="our-work-quote-cta"
                        className="mt-6 inline-flex items-center gap-3 border border-[#FFE600] bg-[#FFE600]/[0.06] hover:bg-[#FFE600] hover:text-black px-8 py-4 font-mono text-xs md:text-sm tracking-[0.28em] uppercase transition-colors">
                        Get A Free Painting Quote →
                    </a>
                </div>
            </section>
        </div>
    );
}
